import { CategoriesResponse, Category } from '../types/category'
import { Product } from '../types/product'
import { extractProducts, pickProductBySlug } from './extractProducts'
import { filterProductsBySearch } from '../utils/productSearch'
import { Shop, ShopsResponse } from '../types/shop'
import { apiConfig } from '../config/app'
import {
  PromoApiError,
  PromoValidateRequest,
  PromoValidateResponse,
} from '../types/promo'

export type FilterSchemaValue = {
  value: string
  label: string
}

export type FilterSchemaField = { values: FilterSchemaValue[] } | { from: number; to: number }

export type FilterSchema = Record<string, FilterSchemaField>

export interface ProductsParams {
  limit?: number
  offset?: number
  category?: string
  beanType?: string
  processingMethod?: string
  originCountry?: string
  minPrice?: number
  maxPrice?: number
  minScaScore?: number
  maxScaScore?: number
  tags?: string[]
  slug?: string
}

export interface CategoriesParams {
  limit?: number
  offset?: number
}

export interface ShopsParams {
  limit?: number
  offset?: number
}

export class ApiService {
  // Simple in-memory cache for GET requests keyed by full URL
  private static getCache: Map<string, Promise<any> | any> = new Map()

  // Auth helpers for deciding anon vs authenticated endpoints
  private static hasAuthToken(): boolean {
    const accessToken = localStorage.getItem('accessToken')
    const idToken = localStorage.getItem('idToken')
    return !!(accessToken && accessToken.trim()) || !!(idToken && idToken.trim())
  }

  private static getAuthHeaders(): HeadersInit {
    const accessToken = localStorage.getItem('accessToken')
    const idToken = localStorage.getItem('idToken')

    const headers: HeadersInit = {}
    if (accessToken && accessToken.trim() !== '') {
      headers['Authorization'] = `Bearer ${accessToken}`
    } else if (idToken && idToken.trim() !== '') {
      headers['Authorization'] = `Bearer ${idToken}`
    }
    return headers
  }

  private static getBaseUrl(): string {
    const isAuthenticated = this.hasAuthToken()
    if (isAuthenticated) {
      return apiConfig.baseUrl
    } else {
      // Use anon endpoint for unauthorized requests
      return apiConfig.baseUrl.replace('/api/ecom', '/api/ecom/anon')
    }
  }

  /** Root API origin + `/api` (without `/ecom` segment), e.g. `https://host/api` */
  private static getApiRootUrl(): string {
    const base = new URL(apiConfig.baseUrl)
    const path = base.pathname.replace(/\/?ecom\/?$/, '/').replace(/\/+$|^\/+/, '/')
    return `${base.origin}${path}`.replace(/\/$/, '')
  }

  static async submitContactRequest(payload: ContactRequestPayload): Promise<void> {
    const url = `${this.getApiRootUrl()}/contact-requests`
    const response = await fetch(url, {
      method: 'POST',
      mode: 'cors',
      headers: {
        'Content-Type': 'application/json',
        accept: 'application/json',
        Origin: window.location.origin,
        Referer: window.location.href,
        ...this.getAuthHeaders(),
      },
      body: JSON.stringify(payload),
    })

    if (!response.ok) {
      let message = `Помилка сервера (${response.status})`
      try {
        const text = await response.text()
        if (text) {
          try {
            const data = JSON.parse(text) as { message?: string; title?: string; error?: string }
            message = data.message || data.title || data.error || message
          } catch {
            message = text.slice(0, 200)
          }
        }
      } catch {
        // keep default message
      }
      throw new Error(message)
    }

    const contentType = response.headers.get('content-type')
    if (contentType?.includes('application/json')) {
      await response.json().catch(() => undefined)
    }
  }

  static async getContactRequests(params: ContactRequestsParams = {}): Promise<ContactRequestEntity[]> {
    const { limit = 50, offset = 0 } = params
    const url = `${this.getApiRootUrl()}/contact-requests?limit=${limit}&offset=${offset}`

    const response = await this.makeRequest<unknown>(url)

    if (Array.isArray(response)) {
      return response as ContactRequestEntity[]
    }

    if (response && typeof response === 'object') {
      const data = response as {
        items?: unknown
        data?: unknown
        contactRequests?: unknown
      }

      if (Array.isArray(data.items)) {
        return data.items as ContactRequestEntity[]
      }
      if (Array.isArray(data.data)) {
        return data.data as ContactRequestEntity[]
      }
      if (Array.isArray(data.contactRequests)) {
        return data.contactRequests as ContactRequestEntity[]
      }
    }

    throw new Error('Invalid API response structure for contact requests')
  }

  private static async makeRequest<T>(url: string): Promise<T> {
    try {
      // Return cached result or pending promise if present
      const cached = this.getCache.get(url)
      if (cached) {
        return cached as T
      }

      const pending = fetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          // Attach auth header if present
          ...this.getAuthHeaders(),
        },
      })
        .then(async response => {
          if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status} - ${response.statusText}`)
          }
          return response.json()
        })
        .then(data => {
          // Cache the resolved data for subsequent calls
          this.getCache.set(url, data)
          return data
        })
        .catch(err => {
          // Remove failed pending entry from cache
          this.getCache.delete(url)
          throw err
        })

      // Store pending promise to de-duplicate concurrent requests
      this.getCache.set(url, pending)
      return (await pending) as T
    } catch (error) {
      if (error instanceof TypeError && error.message.includes('fetch')) {
        throw new Error('Network error: Unable to connect to the server')
      }
      throw error
    }
  }

  private static async postRequest<T>(url: string, body: unknown): Promise<T> {
    try {
      const response = await fetch(url, {
        method: 'POST',
        mode: 'cors',
        headers: {
          'Content-Type': 'application/json',
          accept: 'application/json',
          Origin: window.location.origin,
          Referer: window.location.href,
          // Attach auth header if present
          ...this.getAuthHeaders(),
        },
        body: JSON.stringify(body),
      })

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status} - ${response.statusText}`)
      }

      const data = await response.json()
      return data
    } catch (error) {
      if (error instanceof TypeError && error.message.includes('fetch')) {
        throw new Error('Network error: Unable to connect to the server')
      }
      throw error
    }
  }

  // Методи для продуктів
  static async getProducts(params: ProductsParams = {}): Promise<Product[]> {
    const {
      limit = 50,
      offset = 0,
      category,
      beanType,
      processingMethod,
      originCountry,
      minPrice,
      maxPrice,
      minScaScore,
      maxScaScore,
      tags,
      slug,
    } = params

    let url = `${this.getBaseUrl()}/products?limit=${limit}&offset=${offset}`

    if (slug) {
      url += `&slug=${encodeURIComponent(slug)}`
    }
    if (category) {
      url += `&category=${category}`
    }
    if (beanType) {
      url += `&beanType=${beanType}`
    }
    if (processingMethod) {
      url += `&processingMethod=${processingMethod}`
    }
    if (originCountry) {
      url += `&originCountry=${originCountry}`
    }
    if (minPrice !== undefined) {
      url += `&minPrice=${minPrice}`
    }
    if (maxPrice !== undefined) {
      url += `&maxPrice=${maxPrice}`
    }
    if (minScaScore !== undefined) {
      url += `&minScaScore=${minScaScore}`
    }
    if (maxScaScore !== undefined) {
      url += `&maxScaScore=${maxScaScore}`
    }
    if (tags && tags.length > 0) {
      url += `&tags=${tags.join(',')}`
    }

    const response = await this.makeRequest<any>(url)
    const products = extractProducts(response)
    if (products.length === 0 && response && typeof response === 'object' && !Array.isArray(response) && !('products' in response)) {
      throw new Error('Invalid API response structure')
    }
    return products
  }

  static async getProductBySlug(slug: string): Promise<Product | null> {
    const response = await this.makeRequest<unknown>(
      `${this.getBaseUrl()}/products?slug=${encodeURIComponent(slug)}&limit=1`,
    )
    return pickProductBySlug(response, slug)
  }

  // New: fetch products and filter schema (when provided by the API)
  static async getProductsAndSchema(
    params: ProductsParams = {},
  ): Promise<{ products: Product[]; schema?: FilterSchema }> {
    const {
      limit = 50,
      offset = 0,
      category,
      beanType,
      processingMethod,
      originCountry,
      minPrice,
      maxPrice,
      minScaScore,
      maxScaScore,
      tags,
      slug,
    } = params

    let url = `${this.getBaseUrl()}/products?limit=${limit}&offset=${offset}`

    if (slug) {
      url += `&slug=${encodeURIComponent(slug)}`
    }
    if (category) {
      url += `&category=${category}`
    }
    if (beanType) {
      url += `&beanType=${beanType}`
    }
    if (processingMethod) {
      url += `&processingMethod=${processingMethod}`
    }
    if (originCountry) {
      url += `&originCountry=${originCountry}`
    }
    if (minPrice !== undefined) {
      url += `&minPrice=${minPrice}`
    }
    if (maxPrice !== undefined) {
      url += `&maxPrice=${maxPrice}`
    }
    if (minScaScore !== undefined) {
      url += `&minScaScore=${minScaScore}`
    }
    if (maxScaScore !== undefined) {
      url += `&maxScaScore=${maxScaScore}`
    }
    if (tags && tags.length > 0) {
      url += `&tags=${tags.join(',')}`
    }

    const response = await this.makeRequest<any>(url)

    const products = extractProducts(response)
    let schema: FilterSchema | undefined

    if (response && typeof response === 'object' && 'schema' in response) {
      const maybeSchema = (response as { schema?: unknown }).schema
      if (maybeSchema && typeof maybeSchema === 'object') {
        schema = maybeSchema as FilterSchema
      }
    }

    return { products, schema }
  }

  static async getProductsByCategory(category: string, limit: number = 50): Promise<Product[]> {
    return this.getProducts({ category, limit })
  }

  static async getCoffeeProducts(limit: number = 50): Promise<Product[]> {
    return this.getProductsByCategory('coffee', limit)
  }

  static async getDripProducts(limit: number = 50): Promise<Product[]> {
    return this.getProductsByCategory('drip', limit)
  }

  static async getCapsuleProducts(limit: number = 50): Promise<Product[]> {
    return this.getProductsByCategory('kapsuli', limit)
  }

  // Новий метод для отримання продуктів з фільтрами
  static async getFilteredProducts(
    filters: ProductsParams,
    limit: number = 50,
  ): Promise<Product[]> {
    return this.getProducts({ ...filters, limit })
  }

  // Метод для отримання продуктів за ціною
  static async getProductsByPriceRange(
    minPrice: number,
    maxPrice: number,
    limit: number = 50,
  ): Promise<Product[]> {
    return this.getProducts({ minPrice, maxPrice, limit })
  }

  // Метод для отримання продуктів за країною походження
  static async getProductsByOrigin(originCountry: string, limit: number = 50): Promise<Product[]> {
    return this.getProducts({ originCountry, limit })
  }

  // Метод для отримання продуктів за типом бобів
  static async getProductsByBeanType(beanType: string, limit: number = 50): Promise<Product[]> {
    return this.getProducts({ beanType, limit })
  }

  // Метод для отримання продуктів за методом обробки
  static async getProductsByProcessingMethod(
    processingMethod: string,
    limit: number = 50,
  ): Promise<Product[]> {
    return this.getProducts({ processingMethod, limit })
  }

  static async getProductsByScaScore(
    minScore: number,
    maxScore: number,
    limit: number = 50,
  ): Promise<Product[]> {
    return this.getProducts({ minScaScore: minScore, maxScaScore: maxScore, limit })
  }

  // Page config
  static async getConfig(): Promise<{ bannerUrls: string[] }> {
    const url = `${this.getBaseUrl()}/config`
    return this.makeRequest<{ bannerUrls: string[] }>(url)
  }

  // Методи для категорій
  static async getCategories(params: CategoriesParams = {}): Promise<Category[]> {
    const { limit = 50, offset = 0 } = params

    const url = `${this.getBaseUrl()}/categories?limit=${limit}&offset=${offset}`
    const response = await this.makeRequest<CategoriesResponse>(url)

    // Handle case where response might not have the expected structure
    if (response && typeof response === 'object' && 'categories' in response) {
      const categories = response.categories
      if (Array.isArray(categories)) {
        return categories
      } else {
        throw new Error('Invalid API response: categories is not an array')
      }
    } else if (Array.isArray(response)) {
      // Fallback: if response is directly an array, return it
      return response
    } else {
      // Unexpected API response structure
      throw new Error('Invalid API response structure')
    }
  }

  static async getCategoryByInternalName(internalName: string): Promise<Category | null> {
    try {
      const categories = await this.getCategories({ limit: 50 })
      return categories.find(category => category.internalName === internalName) || null
    } catch (error) {
      return null
    }
  }

  // Методи для магазинів
  static async getShops(params: ShopsParams = {}): Promise<Shop[]> {
    const { limit = 50, offset = 0 } = params

    const url = `${this.getBaseUrl()}/shops?limit=${limit}&offset=${offset}`
    const response = await this.makeRequest<ShopsResponse>(url)

    // Handle case where response might not have the expected structure
    if (response && typeof response === 'object' && 'data' in response) {
      const shops = response.data
      if (Array.isArray(shops)) {
        return shops
      } else {
        throw new Error('Invalid API response: data is not an array')
      }
    } else if (Array.isArray(response)) {
      // Fallback: if response is directly an array, return it
      return response
    } else {
      throw new Error('Invalid API response structure')
    }
  }

  // Search products
  static async searchProducts(query: string, limit: number = 50): Promise<Product[]> {
    try {
      // Отримуємо всі продукти та фільтруємо їх на основі пошукового запиту
      const allProducts = await this.getProducts({ limit: 100 })

      if (!Array.isArray(allProducts)) {
        return []
      }

      return filterProductsBySearch(allProducts, query).slice(0, limit)
    } catch (error) {
      return []
    }
  }

  // Promo codes
  static async validatePromoCode(
    request: PromoValidateRequest,
  ): Promise<PromoValidateResponse> {
    // Authenticated users hit `/api/ecom/user/promo/validate`,
    // anons hit `/api/ecom/anon/promo/validate` (getBaseUrl() swaps in /anon).
    const isAuthenticated = this.hasAuthToken()
    const path = isAuthenticated ? '/user/promo/validate' : '/promo/validate'
    const url = `${this.getBaseUrl()}${path}`
    const normalized: PromoValidateRequest = {
      ...request,
      code: (request.code || '').trim().toUpperCase(),
    }
    return this.postRequest<PromoValidateResponse>(url, normalized)
  }

  // Orders
  static async createOrder(order: OrderPayload): Promise<OrderResponse> {
    const isAuthenticated = this.hasAuthToken()
    const path = isAuthenticated ? '/user/orders' : '/orders'
    const url = `${this.getBaseUrl()}${path}`

    try {
      const response = await fetch(url, {
        method: 'POST',
        mode: 'cors',
        headers: {
          'Content-Type': 'application/json',
          accept: 'application/json',
          Origin: window.location.origin,
          Referer: window.location.href,
          ...this.getAuthHeaders(),
        },
        body: JSON.stringify(order),
      })

      if (response.status === 422) {
        // Promo rejected on order creation — server returns { errorCode }
        let errorCode = 'PROMO_NOT_FOUND'
        try {
          const body = await response.json()
          if (body && typeof body.errorCode === 'string') {
            errorCode = body.errorCode
          }
        } catch {
          // ignore json parse errors — fall back to default
        }
        throw new PromoApiError(errorCode, 422)
      }

      if (!response.ok) {
        throw new Error(
          `HTTP error! status: ${response.status} - ${response.statusText}`,
        )
      }

      return (await response.json()) as OrderResponse
    } catch (error) {
      if (error instanceof PromoApiError) {
        throw error
      }
      if (error instanceof TypeError && error.message.includes('fetch')) {
        throw new Error('Network error: Unable to connect to the server')
      }
      throw error
    }
  }

  static async getOrders(): Promise<OrderResponse[]> {
    const isAuthenticated = this.hasAuthToken()
    if (isAuthenticated) {
      const url = `${this.getBaseUrl()}/user/orders`
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          ...this.getAuthHeaders(),
        },
      })
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status} - ${response.statusText}`)
      }
      return response.json()
    } else {
      const url = `${this.getBaseUrl()}/orders`
      return this.makeRequest<OrderResponse[]>(url)
    }
  }

  static async getOrderByNumber(orderNumber: string): Promise<OrderResponse> {
    const isAuthenticated = this.hasAuthToken()
    if (isAuthenticated) {
      const url = `${this.getBaseUrl()}/user/orders/number/${orderNumber}`
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          ...this.getAuthHeaders(),
        },
      })
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status} - ${response.statusText}`)
      }
      return response.json()
    } else {
      const url = `${this.getBaseUrl()}/orders/number/${orderNumber}`
      return this.makeRequest<OrderResponse>(url)
    }
  }

  // Generate anonymous ID for orders
  static generateAnonymousId(): string {
    return `anon_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
  }
}

// Order payload types
export interface OrderItemMetaData {
  key: string
  value: string
}

export interface OrderItemPayload {
  productId: string
  title: string
  icon: string
  unitPrice: number
  quantity: number
  grindType?: string
  packageWeight?: string
  meta_data?: OrderItemMetaData[]
  // Paid line total (unitPrice * quantity). Optional/backward-compatible.
  totalAmount?: number
  // Catalog (product) discount snapshot — informational, already baked into
  // unitPrice. Independent of promo codes. Absent on older orders (treat as 0).
  basePrice?: number // original unit price before catalog discount
  discountPercent?: number // percent (20 = 20%), NOT a fraction
  discountPerUnit?: number // currency saved per unit
  discountAmount?: number // line discount = discountPerUnit * quantity
}

export interface ShippingAddressPayload {
  fullName: string
  line1: string
  line2: string
  city: string
  state: string
  postalCode: string
  countryCode: string
  phone: string
  // Additional address fields for comprehensive data
  id?: string
  isPrimary?: boolean
  region?: string
  deliveryType?: string
  deliverySubtype?: string
  shippingCity?: string
  warehouseId?: string
  warehouseLabel?: string
  shippingStreet: string
  // Backend schema fields
  shippingHouse?: string      // Shipping house (for courier)
  shippingEntrance?: string   // Shipping entrance (optional)
  shippingApartment?: string  // Shipping apartment (optional)
  shippingComment?: string
}

export interface OrderPayload {
  orderNumber: string
  status: string
  statusLabel: string
  fulfillmentStatus: string
  shipmentStatus: string
  amount: number
  discountAmount: number
  grandTotalAmount: number
  // Aggregate catalog (product) discount across all items — informational only,
  // already reflected in item prices. NOT subtracted again from grandTotalAmount
  // and kept separate from the promo-code discountAmount. Optional/backward-compatible.
  productDiscountAmount?: number
  shippingAddressId: string
  shippingAddress: ShippingAddressPayload
  notesPrivate: string
  shopId: string
  items: OrderItemPayload[]
  anonymous_id?: string
  deliveryType: 'Самовивіз' | 'Нова Пошта'
  deliverySubtype: 'Кур\'єр' | 'Відділення' | 'Поштомат'
  phone: string
  email: string
  firstName: string
  lastName: string
  advertReady?: boolean
  disturbReady?: boolean
  promoCode?: string
  analyticsContext?: import('../analytics/purchaseContext').AnalyticsContextPayload
}

export interface OrderResponse extends OrderPayload {
  id?: string
  redirectUrl?: string
  paymentFormData?: Record<string, string> // Form data for POST submission
  customerId?: string
  createdAt?: string
  updatedAt?: string
}

export interface ContactRequestPayload {
  firstName: string
  lastName: string
  phone: string
  email: string
  company?: string
  request?: string
  requestType: string
  message: string
}

export interface ContactRequestEntity {
  id?: string
  firstName: string
  lastName: string
  phone: string
  email: string
  company?: string
  request?: string
  message: string
  createdAt?: string
}

export interface ContactRequestsParams {
  limit?: number
  offset?: number
}
